import React from "react";
import { useTranslation } from "react-i18next";
import { TbTrash } from "react-icons/tb";
import { GetLanguageString } from "../../../helper/Components";
import { employeeNames, entryTitle, formatRs, isDeductionLine } from "./salaryTransferUtils";

const styles = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    overflow: "hidden",
    marginBottom: "14px",
    background: "#ffffff",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    flexWrap: "wrap",
  },
  avatar: (color) => ({
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: color,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "15px",
    flexShrink: 0,
  }),
  name: { fontWeight: 700, color: "#1f2937", fontSize: "14px", flex: 1, minWidth: 0 },
  amountLabel: { fontSize: "10.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px" },
  amount: { fontSize: "16px", fontWeight: 800, color: "#047857" },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "#ffffff",
    color: "#bb0e0e",
    border: "1px solid #f5b5bb",
    borderRadius: "6px",
    padding: "6px 12px",
    fontWeight: 700,
    fontSize: "12px",
  },
  th: { padding: "8px", borderBottom: "1px solid #e7eaec", color: "#4b5563", fontWeight: 600 },
  td: { padding: "8px", borderTop: "1px solid #f0f1f3", verticalAlign: "middle" },
  typePill: (isDeduction) => ({
    background: isDeduction ? "#fef2f2" : "#ecfdf5",
    color: isDeduction ? "#b91c1c" : "#047857",
    borderRadius: "12px",
    padding: "3px 10px",
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  }),
  removed: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px dashed #f5b5bb",
    background: "#fef2f2",
    borderRadius: "10px",
    padding: "10px 16px",
    color: "#7f1d1d",
    marginBottom: "14px",
  },
  undoBtn: {
    background: "#ffffff",
    border: "1px solid #e7eaec",
    color: "#18A689",
    fontWeight: 700,
    borderRadius: "6px",
    padding: "5px 12px",
  },
};

/**
 * One salary (an FA quick entry), titled by the employee name(s) from dimension 2.
 * Amounts are fixed; the user can only write a memo per line or remove the whole salary.
 */
const SalaryEntryCard = ({ entry, color, removed, memos, onMemoChange, onToggleRemove, disabled }) => {
  const { t } = useTranslation();
  const title = entryTitle(entry);
  const showEmployee = employeeNames(entry).length > 1;

  if (removed) {
    return (
      <div style={styles.removed}>
        <TbTrash color="#bb0e0e" size={16} />
        <div style={{ flex: 1 }}>
          <strong>
            <GetLanguageString props="finance_salary_removed" />:
          </strong>{" "}
          {title} · {formatRs(entry.amount)}
        </div>
        <button type="button" style={styles.undoBtn} onClick={onToggleRemove} disabled={disabled}>
          <GetLanguageString props="finance_salary_undo" />
        </button>
      </div>
    );
  }

  return (
    <article style={styles.card}>
      <div style={styles.header}>
        <span style={styles.avatar(color)}>{title.charAt(0).toUpperCase()}</span>
        <div style={styles.name}>{title}</div>
        <div style={{ textAlign: "right" }}>
          <div style={styles.amountLabel}>
            <GetLanguageString props="finance_salary_net_payment" />
          </div>
          <div style={styles.amount}>{formatRs(entry.amount)}</div>
        </div>
        <button type="button" style={styles.removeBtn} onClick={onToggleRemove} disabled={disabled}>
          <TbTrash color="#bb0e0e" size={14} />
          <GetLanguageString props="finance_salary_remove" />
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              {showEmployee && (
                <th style={{ ...styles.th, paddingLeft: "16px" }}>
                  <GetLanguageString props="finance_salary_employee" />
                </th>
              )}
              <th style={{ ...styles.th, paddingLeft: showEmployee ? "8px" : "16px", width: "140px" }}>
                <GetLanguageString props="finance_salary_type" />
              </th>
              <th style={{ ...styles.th, textAlign: "right", width: "140px" }}>
                <GetLanguageString props="funds_add_fund_amount" />
              </th>
              <th style={{ ...styles.th, paddingRight: "16px" }}>
                <GetLanguageString props="finance_salary_line_memo" />
              </th>
            </tr>
          </thead>
          <tbody>
            {(entry.lines || []).map((line) => {
              const isDeduction = isDeductionLine(line);
              return (
                <tr key={line.lineId}>
                  {showEmployee && (
                    <td style={{ ...styles.td, paddingLeft: "16px", fontWeight: 600, color: "#1f2937" }}>
                      {line.dimension2Name || "—"}
                    </td>
                  )}
                  <td style={{ ...styles.td, paddingLeft: showEmployee ? "8px" : "16px" }}>
                    <span style={styles.typePill(isDeduction)}>
                      <GetLanguageString props={isDeduction ? "finance_salary_deduction" : "finance_salary_type_salary"} />
                    </span>
                  </td>
                  <td
                    style={{
                      ...styles.td,
                      textAlign: "right",
                      fontWeight: 700,
                      color: isDeduction ? "#b91c1c" : "#1f2937",
                    }}
                  >
                    {formatRs(line.amount)}
                  </td>
                  <td style={{ ...styles.td, paddingRight: "16px" }}>
                    <input
                      type="text"
                      aria-label={t("finance_salary_line_memo")}
                      className="form-control"
                      style={{ fontSize: "12.5px", padding: "5px 10px" }}
                      placeholder={t("finance_salary_memo_placeholder")}
                      value={memos[line.lineId] ?? ""}
                      maxLength={250}
                      disabled={disabled}
                      onChange={(e) => onMemoChange(line.lineId, e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
};

export default SalaryEntryCard;
